import React, { useRef, useCallback, useMemo, useEffect, useState, Fragment, useContext } from "react";
import { useSearchParams } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import AppEmptyState from "components/app-empty-state";
import { AxiosContext } from "contexts/with-interceptor-provider";
import AppTable, { AppTableCell, AppTableEditIcon, AppTableMoreIcon } from "components/app-table";
import AppUploadCertModal from "components/pages/e-learning/app-upload-cert-modal";
import uploadIcon from "assets/images/upload-icon.svg";
import { Menu, MenuItem } from "@mui/material";
import AppAssignmentReadyModal from "components/pages/e-learning/app-assignment-ready-modal";
import AppStatus from "components/app-status";
import { formatDate } from "common/calendar";

const PageOngoingAssignments = (props) => {
    const context = useContext(AxiosContext);
    const uploadCertificateRef = useRef();
    const confirmAssignmentRef = useRef();
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const paramsRef = useRef({ page: parseInt(searchParams.get("page")) || 1, sort: searchParams.get("sort") || "" });
    const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
    const [anchorEl, setAnchorEl] = useState();
    const [selectedRow, setSelectedRow] = useState();
    const [loading, setLoading] = useState(true);
    const isEmptyState = useMemo(() => !loading && data.items.length === 0, [loading, data]);

    const onHandleGetList = useCallback(async () => {
        let response = null;

        setLoading(true);
        setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

        try {
            const payload = { ...paramsRef.current, size: 10 };

            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

            payload.page = paramsRef.current.page - 1;

            response = await api.get.assignment.ongoingList(sanitizeObject(payload));
        } catch (error) {
            const wasCancelled = serveRequestErrors(error);
            if (wasCancelled) return;
        }

        if (response) {
            setData((prev) => ({
                ...prev,
                page: paramsRef.current.page,
                prev: !response.first,
                next: !response.last,
                items: response.content,
                total: response.totalPages,
                totalElements: response.totalElements,
                offset: response?.pageable?.offset || 0,
                status: response?.pageable,
            }));
        }

        setLoading(false);
    }, []);

    //prettier-ignore
    const onHandlePagination = useCallback((event) => {
        const control = event.currentTarget?.getAttribute("data-ctrl");

        if (control) {
            if (control === "prev") {
                if (paramsRef.current.page <= 1) return;
                paramsRef.current.page -= 1;
            } else {
                if (paramsRef.current.page >= data.total) return;
                paramsRef.current.page += 1;
            }
        } else {
            paramsRef.current.page = event.target.value;
        }

        onHandleGetList();
    }, [onHandleGetList, data.total]);

    //prettier-ignore
    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";

        paramsRef.current.page = 1;

        onHandleGetList();
    }, [onHandleGetList]);

    //prettier-ignore
    const onHandleReady = useCallback((row) => {
        confirmAssignmentRef.current?.onHandleShow(row.id);
        setAnchorEl(null);
    }, []);

    //prettier-ignore
    const onHandleUpload = useCallback((row) => {
        uploadCertificateRef.current?.onHandleShow(row.id);
    }, []);

    //prettier-ignore
    const onHandleCloseMenu = useCallback(() => {
        setAnchorEl(null);
    }, []);

    //prettier-ignore
    const onHandleTableMenu = useCallback((event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    }, []);

    //prettier-ignore
    const ExamRequiredCell = useCallback(({ row }) => {
        return <AppTableCell center value={row.original.isExamRequired ? "Yes" : "No"} />
    }, [])

    //prettier-ignore
    const StatusCell = useCallback(({ row }) => {
        const status = row.original?.status;
        return (<AppStatus status={status ? status : "-"} />);
    }, []);

    //prettier-ignore
    const StartDateCell = useCallback(({ row }) => {
        const startDate = row.original?.startDate;
        return <AppTableCell center value={startDate ? formatDate(startDate) : "-"} />;
    }, [])

    //prettier-ignore
    const EndDateCell = useCallback(({ row }) => {
        const endDate = row.original?.endDate;
        return <AppTableCell center value={endDate ? formatDate(endDate) : "-"} />;
    }, [])

    //prettier-ignore
    const MenuCell = useCallback(({ row }) => {
        if (row.original.isExamRequired) {
            if (row.original.status === COMMON.STATUS_ID.ASSIGNED) {
                return <AppTableMoreIcon onClick={(event) => onHandleTableMenu(event, row.original)} />;
            } else {
                return <AppTableEditIcon icon={uploadIcon} onClick={() => onHandleUpload(row.original)} />;
            }
        } else {
            return <AppTableEditIcon icon={uploadIcon} onClick={() => onHandleUpload(row.original)} />;
        }
    }, [onHandleUpload, onHandleTableMenu]);

    //prettier-ignore
    const columns = useMemo(() => [
        {
            Header: "Course",
            accessor: "courseTitle",
            disableSortBy: false,
        },
        {
            Header: "Start",
            accessor: "startDate",
            disableSortBy: false,
            Cell: StartDateCell,
        },
        {
            Header: "End",
            accessor: "endDate",
            disableSortBy: false,
            Cell: EndDateCell,
        },
        {
            Header: "Days Left",
            accessor: "daysLeft",
            disableSortBy: true,
        },
        {
            Header: "Exam Required",
            accessor: "isExamRequired",
            disableSortBy: true,
            Cell: ExamRequiredCell,
        },
        {
            Header: "Assigned By",
            accessor: "assignedByName",
            disableSortBy: true,
        },
        {
            Header: "Status",
            accessor: "status",
            disableSortBy: false,
            Cell: StatusCell,
        },
        {
            Header: "",
            accessor: "*",
            disableSortBy: true,
            Cell: MenuCell,
        },
    ], [MenuCell, StatusCell, ExamRequiredCell, StartDateCell, EndDateCell]);

    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        return () => {
            context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.ASSIGNMENT.ONGOING_LIST);
        };
    }, [context]);

    return (
        <div className="page-ongoing-assignments">
            <div className="ongoing-assignments">
                {isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton={true} />}

                {!isEmptyState && (
                    <Fragment>
                        <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
                        <Menu classes={{ root: "app-table-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onHandleCloseMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} >
                            <MenuItem onClick={() => onHandleReady(selectedRow)} > Mark as Ready </MenuItem>
                        </Menu>
                        <AppUploadCertModal ref={uploadCertificateRef} onHandleGetList={onHandleGetList} />
                        <AppAssignmentReadyModal ref={confirmAssignmentRef} onHandleGetList={onHandleGetList} />
                    </Fragment>
                )}
            </div>
        </div>
    );
};

export default PageOngoingAssignments;
